import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { usePartnerFormStore } from "../store/usePartnerFormStore";
import { Input, NavigationButtons } from "./ui";
import { checkExistingFormByPhone } from "../apis/partnerForm";

// Validation helpers
const validateFullName = (name: string): string | null => {
  if (!name || name.trim().length < 2) {
    return "Full name must be at least 2 characters";
  }
  if (!/^[a-zA-Z\s.'-]+$/.test(name.trim())) {
    return "Name can only contain letters, spaces, and basic punctuation";
  }
  return null;
};

const validateBusinessName = (name: string): string | null => {
  if (!name || name.trim().length === 0) {
    return "Business name is required";
  }
  if (name.trim().length < 2) {
    return "Business name must be at least 2 characters";
  }
  return null;
};

const validateDistrict = (district: string): string | null => {
  if (!district || district.trim().length === 0) {
    return "District is required";
  }
  if (district.trim().length < 2) {
    return "District must be at least 2 characters";
  }
  return null;
};

const validateMobileNumber = (number: string): string | null => {
  if (!number) {
    return "Mobile number is required";
  }
  const cleanNumber = number.replace(/\s/g, '');
  if (!/^[6-9]\d{9}$/.test(cleanNumber)) {
    if (cleanNumber.length < 10) {
      return `Enter ${10 - cleanNumber.length} more digit${10 - cleanNumber.length > 1 ? 's' : ''}`;
    }
    if (cleanNumber.length > 10) {
      return "Mobile number must be exactly 10 digits";
    }
    return "Must start with 6, 7, 8, or 9";
  }
  return null;
};

const validateWhatsAppNumber = (number: string | undefined): string | null => {
  if (!number || number.trim() === '') {
    return null; // Optional field
  }
  const cleanNumber = number.replace(/\s/g, '');
  if (!/^[6-9]\d{9}$/.test(cleanNumber)) {
    if (cleanNumber.length < 10) {
      return `Enter ${10 - cleanNumber.length} more digit${10 - cleanNumber.length > 1 ? 's' : ''}`;
    }
    if (cleanNumber.length > 10) {
      return "Must be exactly 10 digits";
    }
    return "Must start with 6, 7, 8, or 9";
  }
  return null;
};

const validateGSTNumber = (gstNumber: string | undefined): string | null => {
  if (!gstNumber || gstNumber.trim() === '') {
    return null; // Optional field
  }
  const cleanGST = gstNumber.trim().toUpperCase();
  // GST format: 2 digits state code, 10 digits PAN, 1 digit entity code, 1 digit blank, 1 digit checksum
  const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
  if (!gstRegex.test(cleanGST)) {
    return "Please enter a valid GST number (e.g., 22AAAAA0000A1Z)";
  }
  return null;
};

const PartnerFormStep1: React.FC = () => {
  const { basicInfo, updateBasicInfo, nextStep, errors, setError, clearError } =
    usePartnerFormStore();

  // Registration verification state
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [isPhoneRegistered, setIsPhoneRegistered] = useState(false);
  const [registeredBusinessName, setRegisteredBusinessName] = useState<string | null>(null);
  const [lastCheckedPhone, setLastCheckedPhone] = useState<string | null>(null);

  // Check if mobile number is already registered
  useEffect(() => {
    const cleanNumber = basicInfo.mobileNumber.replace(/\s/g, '');

    // Only verify when a valid 10-digit number is typed
    if (!/^[6-9]\d{9}$/.test(cleanNumber)) {
      setIsPhoneRegistered(false);
      setRegisteredBusinessName(null);
      setIsCheckingPhone(false);
      return;
    }

    // Skip if already verified this exact number
    if (lastCheckedPhone === cleanNumber) {
      return;
    }

    let isCancelled = false;
    setIsCheckingPhone(true);

    const timer = setTimeout(async () => {
      try {
        const result = await checkExistingFormByPhone(cleanNumber);
        if (isCancelled) return;

        setLastCheckedPhone(cleanNumber);
        if (result && result.isRegistered) {
          setIsPhoneRegistered(true);
          setRegisteredBusinessName(result.businessName || null);
          const errorMsg = result.businessName
            ? `This mobile number is already registered for "${result.businessName}". Please change the number to proceed.`
            : "This mobile number is already registered. Please change the number to proceed.";
          setError("mobileNumber", errorMsg);
        } else {
          setIsPhoneRegistered(false);
          setRegisteredBusinessName(null);
          clearError("mobileNumber");
        }
      } catch (err) {
        if (isCancelled) return;
        console.error("Error checking mobile registration:", err);
      } finally {
        if (!isCancelled) {
          setIsCheckingPhone(false);
        }
      }
    }, 400);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [basicInfo.mobileNumber, lastCheckedPhone, setError, clearError]);

  // Real-time validation states
  const validationStates = useMemo(() => ({
    fullName: {
      error: errors.fullName as string | undefined,
      isValid: basicInfo.fullName.trim().length >= 2 && !validateFullName(basicInfo.fullName),
    },
    businessName: {
      error: errors.businessName as string | undefined,
      isValid: basicInfo.businessName.trim().length >= 2 && !validateBusinessName(basicInfo.businessName),
    },
    mobileNumber: {
      error: errors.mobileNumber as string | undefined,
      isValid:
        /^[6-9]\d{9}$/.test(basicInfo.mobileNumber.replace(/\s/g, '')) &&
        !isPhoneRegistered &&
        !isCheckingPhone &&
        !errors.mobileNumber,
    },
    whatsappNumber: {
      error: errors.whatsappNumber as string | undefined,
      isValid: !basicInfo.whatsappNumber || basicInfo.whatsappNumber.trim() === '' || 
               /^[6-9]\d{9}$/.test(basicInfo.whatsappNumber.replace(/\s/g, '')),
    },
    gstNumber: {
      error: errors.gstNumber as string | undefined,
      isValid: !basicInfo.gstNumber || basicInfo.gstNumber.trim() === '' || 
               !validateGSTNumber(basicInfo.gstNumber),
    },
    employeeCode: {
      error: errors.employeeCode as string | undefined,
      isValid: !basicInfo.employeeCode || basicInfo.employeeCode.trim().length > 0,
    },
    district: {
      error: errors.district as string | undefined,
      isValid: basicInfo.district.trim().length >= 2 && !validateDistrict(basicInfo.district),
    },
  }), [basicInfo, errors, isPhoneRegistered, isCheckingPhone]);

  const validateStep = useCallback((): boolean => {
    let isValid = true;
    const newErrors: Record<string, string> = {};

    const fullNameError = validateFullName(basicInfo.fullName);
    if (fullNameError) {
      newErrors.fullName = fullNameError;
      isValid = false;
    }

    const businessNameError = validateBusinessName(basicInfo.businessName);
    if (businessNameError) {
      newErrors.businessName = businessNameError;
      isValid = false;
    }

    const districtError = validateDistrict(basicInfo.district);
    if (districtError) {
      newErrors.district = districtError;
      isValid = false;
    }

    if (isCheckingPhone) {
      newErrors.mobileNumber = "Verifying mobile number, please wait...";
      isValid = false;
    } else if (isPhoneRegistered) {
      newErrors.mobileNumber = registeredBusinessName
        ? `This mobile number is already registered for "${registeredBusinessName}". Please change the number to proceed.`
        : "This mobile number is already registered. Please change the number to proceed.";
      isValid = false;
    } else {
      const mobileError = validateMobileNumber(basicInfo.mobileNumber);
      if (mobileError) {
        newErrors.mobileNumber = mobileError;
        isValid = false;
      }
    }

    const whatsappError = validateWhatsAppNumber(basicInfo.whatsappNumber);
    if (whatsappError) {
      newErrors.whatsappNumber = whatsappError;
      isValid = false;
    }

    const gstError = validateGSTNumber(basicInfo.gstNumber);
    if (gstError) {
      newErrors.gstNumber = gstError;
      isValid = false;
    }

    // Set errors
    Object.keys(newErrors).forEach((key) => {
      setError(key, newErrors[key]);
    });

    // Clear errors for fields that are now valid
    Object.keys(basicInfo).forEach((key) => {
      if (!newErrors[key]) {
        clearError(key);
      }
    });

    return isValid;
  }, [basicInfo, setError, clearError, isCheckingPhone, isPhoneRegistered, registeredBusinessName]);

  const handleNext = async () => {
    const cleanNumber = basicInfo.mobileNumber.replace(/\s/g, '');

    // If mobile number is 10 digits and hasn't been verified yet, check immediately
    if (/^[6-9]\d{9}$/.test(cleanNumber) && lastCheckedPhone !== cleanNumber) {
      setIsCheckingPhone(true);
      try {
        const result = await checkExistingFormByPhone(cleanNumber);
        setLastCheckedPhone(cleanNumber);
        if (result && result.isRegistered) {
          setIsPhoneRegistered(true);
          setRegisteredBusinessName(result.businessName || null);
          const errorMsg = result.businessName
            ? `This mobile number is already registered for "${result.businessName}". Please change the number to proceed.`
            : "This mobile number is already registered. Please change the number to proceed.";
          setError("mobileNumber", errorMsg);
          setIsCheckingPhone(false);
          return;
        }
      } catch (e) {
        console.error("Verification error in handleNext:", e);
      } finally {
        setIsCheckingPhone(false);
      }
    }

    if (isCheckingPhone) return;
    if (isPhoneRegistered) {
      setError(
        "mobileNumber",
        registeredBusinessName
          ? `This mobile number is already registered for "${registeredBusinessName}". Please change the number to proceed.`
          : "This mobile number is already registered. Please change the number to proceed."
      );
      return;
    }
    if (validateStep()) {
      nextStep();
    }
  };

  const handleChange = (field: keyof typeof basicInfo, value: string) => {
    updateBasicInfo({ [field]: value });
    
    // Real-time validation on change
    let error: string | null = null;
    switch (field) {
      case 'fullName':
        error = value.trim().length > 0 ? validateFullName(value) : null;
        break;
      case 'businessName':
        error = value.trim().length > 0 ? validateBusinessName(value) : null;
        break;
      case 'district':
        error = value.trim().length > 0 ? validateDistrict(value) : null;
        break;
      case 'mobileNumber':
        if (value.replace(/\s/g, '') !== lastCheckedPhone) {
          setIsPhoneRegistered(false);
          setRegisteredBusinessName(null);
        }
        error = value.length > 0 ? validateMobileNumber(value) : null;
        break;
      case 'whatsappNumber':
        error = value.length > 0 ? validateWhatsAppNumber(value) : null;
        break;
      case 'gstNumber':
        error = value.length > 0 ? validateGSTNumber(value) : null;
        break;
      case 'employeeCode':
        error = null; // Optional field
        break;
    }

    if (error) {
      setError(field, error);
    } else {
      clearError(field);
    }
  };

  // Animation variants for staggered children
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h2 className="text-xl md:text-2xl font-semibold text-secondary-900 mb-1">
          Basic Information
        </h2>
        <p className="text-sm md:text-base text-secondary-600">
          Tell us about yourself and your business
        </p>
      </motion.div>

      {/* Form Fields with Staggered Animation */}
      <motion.div
        className="space-y-5"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants}>
          <Input
            label="Full Name"
            placeholder="Enter your full name"
            value={basicInfo.fullName}
            onChange={(e) => handleChange("fullName", e.target.value)}
            error={validationStates.fullName.error}
            isValid={validationStates.fullName.isValid}
            required
            autoComplete="name"
            leftIcon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            }
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <Input
            label="Business Name"
            placeholder="Enter your business name"
            value={basicInfo.businessName}
            onChange={(e) => handleChange("businessName", e.target.value)}
            error={validationStates.businessName.error}
            isValid={validationStates.businessName.isValid}
            required
            autoComplete="organization"
            leftIcon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            }
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <Input
            label="District"
            placeholder="Enter your district"
            value={basicInfo.district}
            onChange={(e) => handleChange("district", e.target.value)}
            error={validationStates.district.error}
            isValid={validationStates.district.isValid}
            required
            autoComplete="address-level2"
            leftIcon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <Input
            label="Mobile Number"
            placeholder="Enter 10-digit mobile number"
            type="tel"
            inputMode="numeric"
            value={basicInfo.mobileNumber}
            onChange={(e) => {
              // Only allow numbers
              const value = e.target.value.replace(/\D/g, '').slice(0, 10);
              handleChange("mobileNumber", value);
            }}
            error={validationStates.mobileNumber.error}
            isValid={validationStates.mobileNumber.isValid}
            isLoading={isCheckingPhone}
            hideErrorMessage={isPhoneRegistered}
            required
            autoComplete="tel"
            helperText={
              isCheckingPhone
                ? "Verifying mobile number registration..."
                : isPhoneRegistered
                  ? undefined
                  : "Indian mobile number starting with 6, 7, 8, or 9"
            }
          />
          {isPhoneRegistered && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2.5 p-3 rounded-xl bg-red-50/90 border border-red-200 text-red-800 text-xs flex items-start gap-2.5 shadow-sm"
            >
              <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="font-semibold text-red-900 text-sm">Mobile Number Already Registered</p>
                <p className="mt-0.5 text-red-700 leading-relaxed">
                  This mobile number is already registered{registeredBusinessName ? ` for "${registeredBusinessName}"` : ""}. Please <span className="font-semibold underline">change the number</span> to continue your registration.
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>

        <motion.div variants={itemVariants}>
          <Input
            label="WhatsApp Number"
            placeholder="Enter WhatsApp number (optional)"
            type="tel"
            inputMode="numeric"
            value={basicInfo.whatsappNumber || ""}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 10);
              handleChange("whatsappNumber", value);
            }}
            error={validationStates.whatsappNumber.error}
            isValid={validationStates.whatsappNumber.isValid && !!basicInfo.whatsappNumber}
            showSuccessIcon={!!basicInfo.whatsappNumber}
            autoComplete="tel"
            helperText="Leave empty if same as mobile number"
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <Input
            label="GST Number"
            placeholder="Enter GST number (optional)"
            value={basicInfo.gstNumber || ""}
            onChange={(e) => {
              const value = e.target.value.toUpperCase();
              handleChange("gstNumber", value);
            }}
            error={validationStates.gstNumber.error}
            isValid={validationStates.gstNumber.isValid && !!basicInfo.gstNumber}
            showSuccessIcon={!!basicInfo.gstNumber}
            autoComplete="off"
            helperText="Format: 22AAAAA0000A1Z (15 characters)"
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <Input
            label="Employee Code"
            placeholder="Enter employee code (if registering on behalf)"
            value={basicInfo.employeeCode || ""}
            onChange={(e) => handleChange("employeeCode", e.target.value)}
            error={validationStates.employeeCode.error}
            showSuccessIcon={!!basicInfo.employeeCode}
            helperText="Reference for staff who submitted registration on partner's behalf"
          />
        </motion.div>
      </motion.div>

      {/* Progress Summary */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="p-3 bg-primary-50/50 rounded-xl border border-primary-100"
      >
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-secondary-700">
              Step 1 of 7
            </p>
            <p className="text-xs text-secondary-500">
              Your information helps us personalize your experience
            </p>
          </div>
        </div>
      </motion.div>

      <NavigationButtons
        onNext={handleNext}
        nextLabel="Next: Service Details"
        showPrevious={false}
        nextDisabled={
          isCheckingPhone ||
          isPhoneRegistered ||
          !validationStates.mobileNumber.isValid ||
          !validationStates.fullName.isValid ||
          !validationStates.businessName.isValid ||
          !validationStates.district.isValid
        }
      />
    </div>
  );
};

export default PartnerFormStep1;




//